import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

/* ================================================
   Category Card
   ================================================ */
function CategoryCard({ cat }: { cat: (typeof categories)[0] }) {
  return (
    <a
      href={cat.url}
      target="_blank"
      rel="noopener nofollow"
      className="group flex flex-col rounded-lg overflow-hidden bg-white border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-36 md:h-40 overflow-hidden bg-background-100">
        <img
          src={cat.image}
          alt={`${cat.name} pandabuy spreadsheet category`}
          title={`Pandabuy Spreadsheet ${cat.name} Category`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        <span className="absolute bottom-2 left-2 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm">
          <i className={`${cat.icon} text-sm text-foreground-600`}></i>
        </span>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors whitespace-nowrap">
          {cat.name}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
          {cat.description}
        </p>
      </div>
    </a>
  );
}

/* ================================================
   Article Teaser Card
   ================================================ */
function ArticleTeaserCard({ article }: { article: (typeof articles)[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-white border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-40 md:h-44 overflow-hidden bg-background-100">
        <img
          src={article.teaserImage}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-white bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {article.tag}
          </span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-2 line-clamp-3 leading-relaxed flex-1">
          {article.teaserExcerpt}
        </p>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 mt-3 group-hover:gap-2 transition-all">
          Read Article
          <i className="ri-arrow-right-line text-xs"></i>
        </span>
      </div>
    </Link>
  );
}

/* ================================================
   Homepage
   ================================================ */
export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.yachts';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "PandaBuy Yachts",
            "url": siteUrl,
            "description": "Smart cross-border shopping comparison platform with curated pandabuy spreadsheets across 14 product categories.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": siteUrl + "/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "publisher": {
              "@type": "Organization",
              "name": "PandaBuy Yachts",
              "url": siteUrl,
              "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
            }
          })
        }}
      />
      <Navbar />

      {/* ========== HERO ========== */}
      <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20photography%20of%20carefully%20curated%20fashion%20items%20including%20cream%20sneakers%20sage%20green%20hoodie%20tan%20leather%20bag%20gold%20accessories%20and%20folded%20linen%20clothing%20artistically%20arranged%20in%20circular%20composition%20on%20warm%20beige%20textured%20paper%20background%20soft%20diffused%20sunlight%20from%20above%20minimalist%20editorial%20style%20harmonious%20warm%20earth%20tones%20high%20detail&width=1600&height=900&seq=pandabuy-hero-v3&orientation=landscape")',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-16 pb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <i className="ri-star-line text-accent-300 text-sm"></i>
            <span className="text-xs md:text-sm text-white/90 font-medium tracking-wide">
              Updated Daily &bull; Free Access
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight [text-shadow:0_2px_8px_rgba(0,0,0,0.4)]">
            Discover the Most Powerful
            <br />
            <span className="text-accent-300">Pandabuy Spreadsheet</span>
            <br />
            for Smart Shopping
          </h1>

          <p className="mt-5 md:mt-6 text-sm md:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed [text-shadow:0_1px_4px_rgba(0,0,0,0.3)]">
            Compare products, track prices, and explore curated shopping spreadsheets to find the best deals across global platforms — all in one place.
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-white text-sm md:text-base font-semibold rounded-full hover:bg-accent-600 transition-all duration-300 shadow-lg shadow-accent-500/25 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white text-sm md:text-base font-semibold rounded-full hover:bg-white/20 border border-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Pandabuy Sheet
              <i className="ri-external-link-line text-sm"></i>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
            <span className="text-xs md:text-sm text-white/60 flex items-center gap-1.5">
              <i className="ri-checkbox-circle-line text-accent-300"></i>
              Trusted by 10,000+ shoppers
            </span>
            <span className="text-xs md:text-sm text-white/60 flex items-center gap-1.5">
              <i className="ri-refresh-line text-accent-300"></i>
              Updated daily spreadsheets
            </span>
            <span className="text-xs md:text-sm text-white/60 flex items-center gap-1.5">
              <i className="ri-lock-unlock-line text-accent-300"></i>
              Free access
            </span>
          </div>
        </div>
      </section>

      {/* ========== VALUE PROPOSITION ========== */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-500">
              Why Use It
            </span>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-foreground-950">
              Why Use a Pandabuy Spreadsheet?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { icon: "ri-time-line", title: "Save Time", desc: "Compare products across platforms instantly" },
              { icon: "ri-coins-line", title: "Track Prices", desc: "Spot price differences and save money" },
              { icon: "ri-database-line", title: "Structured Data", desc: "Avoid overpaying with organized info" },
              { icon: "ri-article-line", title: "Curated Lists", desc: "Access pre-built shopping spreadsheets" },
              { icon: "ri-lightbulb-line", title: "Smarter Decisions", desc: "Make data-driven buying choices" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-3 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50">
                  <i className={`${item.icon} text-lg text-primary-500`}></i>
                </span>
                <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CATEGORY GRID ========== */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-500">
              Shop by Category
            </span>
            <h2 className="mt-3 text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight">
              Browse 14 Curated
              <br />
              Product Categories
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
              Each category links to our comprehensive pandabuy spreadsheet — organized with real product images for effortless comparison shopping.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <CategoryCard key={cat.name} cat={cat} />
            ))}
          </div>
        </div>
      </section>

      {/* ========== MAIN GUIDE TEASER ========== */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-16">
            <div className="flex-1 space-y-5">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary-500 bg-primary-50 px-3 py-1 rounded-full">
                SEO Guide 2026
              </span>
              <h2 className="text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight">
                The Complete
                <br />
                Pandabuy Spreadsheet
                <br />
                Shopping Strategy
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-lg">
                Learn how to leverage a pandabuy spreadsheet to save time, compare prices, and find the best deals across all 14 product categories. Our comprehensive guide walks you through the entire workflow — from browsing to buying smart.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/pandabuy-spreadsheet-guide"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Read Full Guide
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 border-2 border-primary-200 text-primary-700 text-sm font-semibold rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  View All Categories
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[380px] h-72 md:h-80 rounded-xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20with%20laptop%20displaying%20spreadsheet%20data%20on%20screen%20surrounded%20by%20various%20fashion%20items%20cream%20sneakers%20folded%20beige%20sweater%20and%20small%20accessories%20on%20light%20wooden%20desk%20natural%20sunlight%20from%20large%20window%20soft%20shadows%20modern%20editorial%20photography%20warm%20neutral%20tones%20professional%20aesthetic%20high%20detail&width=800&height=640&seq=pandabuy-guide-teaser-v3&orientation=landscape"
                alt="Pandabuy Spreadsheet Shopping Guide"
                title="Pandabuy Spreadsheet Shopping Strategy Guide"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ========== 9 SEO ARTICLE TEASERS ========== */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-500">
              SEO Articles
            </span>
            <h2 className="mt-3 text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight">
              Pandabuy Spreadsheet
              <br />
              Shopping Guides
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
              Explore our collection of in-depth guides covering every aspect of smart cross-border shopping with a pandabuy spreadsheet.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <ArticleTeaserCard key={article.slug} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA SECTION ========== */}
      <section className="relative w-full min-h-[420px] md:min-h-[480px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=Artistic%20abstract%20horizontal%20collage%20of%20modern%20fashion%20and%20lifestyle%20items%20including%20textured%20fabric%20swatches%20leather%20accessories%20ceramic%20objects%20and%20dried%20botanicals%20arranged%20in%20flowing%20organic%20composition%20on%20soft%20cream%20surface%20gentle%20gradient%20overlay%20editorial%20style%20harmonious%20palette%20of%20warm%20neutrals%20dusty%20greens%20and%20soft%20terracotta%20minimalist%20aesthetic%20high%20resolution&width=1600&height=700&seq=pandabuy-cta-v3&orientation=landscape")',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/20"></div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-4 max-w-lg">
              <h2 className="text-2xl md:text-4xl font-extrabold text-white leading-tight [text-shadow:0_2px_6px_rgba(0,0,0,0.4)]">
                Ready to Shop Smarter?
              </h2>
              <p className="text-sm md:text-base text-white/75 leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.3)]">
                Compare prices across global platforms. Find the best deals in seconds with our curated pandabuy spreadsheet.
              </p>
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-foreground-950 text-sm md:text-base font-semibold rounded-full hover:bg-white/90 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              Open kaspreadsheet.com
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950">
                <i className="ri-arrow-right-line text-white text-xs"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <Footer />
    </div>
  );
}